import { computed, type Ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useProductCompleteStore } from '@/features/Product/stores/productComplete';
import { useProductBaseStore } from '@/features/Product/stores/productBase';
import { pickWeighted } from '@/composables/pickWeighted';
import type { AddToCartItem } from '@/features/Cart/types/servicesShopifyCart';
import type {
  CheckoutProductSelection,
  RandomisationDetails,
  RandomisationException,
} from '@/features/Product/types/models';

export const useRandomisation = (
  randomisationDetails: Ref<RandomisationDetails | undefined>,
  exceptionDetails?: Ref<RandomisationException | undefined>,
  otherCartProductHandles?: Ref<string[]>,
  excludedSkus?: Ref<string[]>,
) => {
  const { cachedDisplayProducts, cachedCompleteProducts } = storeToRefs(useProductCompleteStore());
  const { completeBaseProducts } = storeToRefs(useProductBaseStore());
  const productSources = computed(() => [
    ...completeBaseProducts.value,
    ...cachedCompleteProducts.value,
    ...cachedDisplayProducts.value,
  ]);

  const availableOptions = computed(() => {
    const details = randomisationDetails.value;
    if (!details?.options?.length) return [];
    const excluded = excludedSkus?.value ?? [];
    return details.options.filter((option) => {
      if (excluded.includes(option.sku)) return false;
      const product = productSources.value.find((p) => p.attributes.sku === option.sku);
      return product && !product.attributes.soldOut;
    });
  });

  const isRandomisationEnabled = computed(
    () =>
      (!!randomisationDetails.value?.baseSku || !!randomisationDetails.value?.productHandle) &&
      availableOptions.value.length > 0,
  );

  const isExceptionEligible = computed(() => {
    const exception = exceptionDetails?.value;
    const handles = otherCartProductHandles?.value;
    if (!exception || !handles?.length) return false;
    return handles.every((handle) => exception.productHandles.includes(handle));
  });

  const isMysteryItem = (item: CheckoutProductSelection): boolean => {
    const details = randomisationDetails.value;
    if (!details) return false;
    if (details.baseSku && item.sku.startsWith(details.baseSku)) return true;
    if (details.productHandle) {
      return productSources.value.some(
        (p) => p.attributes.sku === item.sku && p.attributes.handle === details.productHandle,
      );
    }
    return false;
  };

  const resolveSkuInBundleDetails = (
    bundleDetails: CheckoutProductSelection[],
  ): CheckoutProductSelection[] => {
    const details = randomisationDetails.value;
    if (!details || (!isRandomisationEnabled.value && !isExceptionEligible.value)) {
      return bundleDetails;
    }
    const selectedCounts = new Map<string, number>();
    const minUniqueProducts = details.minUniqueProducts ?? 0;

    const drawOption = () => {
      const withCapacity = availableOptions.value.filter((option) => {
        const currentCount = selectedCounts.get(option.sku) ?? 0;
        return currentCount < (option.maxQuantity ?? 5);
      });
      const unseen = withCapacity.filter((option) => !selectedCounts.has(option.sku));
      const preferred =
        selectedCounts.size < minUniqueProducts && unseen.length ? unseen : withCapacity;
      return pickWeighted(preferred.length ? preferred : availableOptions.value);
    };

    return bundleDetails.flatMap((item) => {
      if (!isMysteryItem(item)) return [item];
      if (isExceptionEligible.value && exceptionDetails?.value) {
        const { assignedSku } = exceptionDetails.value;
        const resolvedProduct = productSources.value.find(
          (p) => p.attributes.sku === assignedSku.sku,
        );
        if (resolvedProduct) {
          return [
            {
              ...item,
              sku: assignedSku.sku,
              quantity: assignedSku.quantity,
              id: `gid://shopify/ProductVariant/${resolvedProduct.attributes.variantId}` as const,
            },
          ];
        }
      }

      const draws = new Map<string, { variantId: number; quantity: number }>();
      for (let drawIndex = 0; drawIndex < item.quantity; drawIndex++) {
        const resolvedOption = drawOption();
        if (!resolvedOption) break;
        const resolvedProduct = productSources.value.find(
          (p) =>
            p.attributes.sku === resolvedOption.sku &&
            (!details.productHandle || p.attributes.handle === details.productHandle),
        );
        if (!resolvedProduct) break;

        selectedCounts.set(resolvedOption.sku, (selectedCounts.get(resolvedOption.sku) ?? 0) + 1);
        const existingDraw = draws.get(resolvedOption.sku);
        if (existingDraw) {
          existingDraw.quantity += 1;
        } else {
          draws.set(resolvedOption.sku, {
            variantId: resolvedProduct.attributes.variantId,
            quantity: 1,
          });
        }
      }

      if (!draws.size) return [item];

      return [...draws.entries()].map(([sku, draw]) => ({
        ...item,
        sku,
        quantity: draw.quantity,
        id: `gid://shopify/ProductVariant/${draw.variantId}` as const,
      }));
    });
  };

  const resolveRandomisationAtcObject = (items: AddToCartItem[]): AddToCartItem[] => {
    if (!isRandomisationEnabled.value && !isExceptionEligible.value) return items;
    return items.map((item) => {
      const customBundleDetails = item.properties?._custom_bundle_details;
      if (!customBundleDetails) return item;
      try {
        const resolvedDetails = resolveSkuInBundleDetails(JSON.parse(customBundleDetails));
        return {
          ...item,
          properties: {
            ...item.properties,
            _custom_bundle_details: JSON.stringify(resolvedDetails),
          },
        };
      } catch {
        return item;
      }
    });
  };

  return {
    isRandomisationEnabled,
    availableOptions,
    resolveSkuInBundleDetails,
    resolveRandomisationAtcObject,
  };
};
